import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeft, Pencil, Users } from 'lucide-react'
import { useState } from 'react'

import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { MfaResetAction, TemporaryPasswordAction, UserStatusActions } from '@/features/admin/user-account-actions'
import { EditProfileDialog, EditRolesDialog } from '@/features/admin/user-editors'
import { AccessPanel, DevicesPanel, ProfilePanel, SecurityPanel } from '@/features/admin/user-panels'
import { useAdminPermissions } from '@/features/admin/permissions'
import { useAdminUser } from '@/features/admin/queries'

/**
 * One staff account: profile, roles and branch access, security state, and its field devices.
 *
 * Every control on this screen is one of two shapes. A status change, a role change, and amending
 * the profile are dual-controlled and go through `AdminDualControlNotice` /
 * `AdminDualControlOutcome`. Issuing a temporary password and revoking a device are not — the
 * server answers each of those plainly, and the screen reads that answer rather than assuming.
 */
export function AdminUserDetailPage() {
  const { userId } = useParams({ from: '/authenticated/admin/users/$userId' })
  const permissions = useAdminPermissions()
  const [editingProfile, setEditingProfile] = useState(false)
  const [editingRoles, setEditingRoles] = useState(false)

  const query = useAdminUser(userId)

  return (
    <RequiresWiderScreen
      screen="A staff account"
      reason="Roles, branch access, and security controls are read and set together here — a desk screen, not one for a phone."
    >
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All users
      </Link>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This account could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.fullName}
            reference={query.data.username}
            badges={
              <>
                <StatusBadge status={query.data.status} />
                {query.data.mustChangePassword ? <Badge tone="warning">Must change password</Badge> : null}
              </>
            }
            actions={
              <>
                {permissions.canUpdateUser ? (
                  <Button variant="secondary" onClick={() => setEditingProfile(true)}>
                    <Pencil />
                    Amend profile
                  </Button>
                ) : null}
                {permissions.canAssignRoles ? (
                  <Button variant="secondary" onClick={() => setEditingRoles(true)}>
                    <Users />
                    Edit roles
                  </Button>
                ) : null}
                <UserStatusActions userId={query.data.userId} status={query.data.status} />
                <MfaResetAction userId={query.data.userId} mfaEnrolled={query.data.mfaEnrolled} />
                <TemporaryPasswordAction userId={query.data.userId} username={query.data.username} />
              </>
            }
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <ProfilePanel user={query.data} />
            <AccessPanel user={query.data} />
            <SecurityPanel user={query.data} />
            <DevicesPanel user={query.data} />
          </div>

          <EditProfileDialog user={query.data} open={editingProfile} onOpenChange={setEditingProfile} />
          <EditRolesDialog user={query.data} open={editingRoles} onOpenChange={setEditingRoles} />
        </>
      )}
    </RequiresWiderScreen>
  )
}
